<template>
  <Modal
    data-testid="product-order-details"
    class="product-order-details"
    size="md"
    :is-open="isOpen"
    @close="handleClose"
  >
    <LoaderIndicator v-if="isLoading" size="32" with-container />

    <HelperViewStatus
      v-else-if="hasError"
      class="product-order-details__helper"
      generic-error
      button-label="Tentar novamente"
      @click="fetchOrderDetails"
    />

    <HelperViewStatus
      v-else-if="cancelModalOpen"
      class="product-order-details__helper"
      status="negative"
      title="Cancelar"
      description="Sua solicitação será cancelada e o valor permanecerá no seu produto."
    />

    <div v-else class="product-order-details__content">
      <HeadingDisplay
        size="small"
        :head="orderDetails?.heading.head"
        :title="orderDetails?.heading.title"
        :subtitle="orderDetails?.heading.subtitle.text"
      >
        <template #subtitle-component>
          <BaseText size="p5" :color="NebraskaColors.statusInfo">
            {{ orderDetails?.heading.subtitle.text }}
          </BaseText>
        </template>
      </HeadingDisplay>
      <div class="product-order-details__list">
        <ListDisplay
          v-for="(item, index) in orderDetails?.details.list"
          :key="index"
          class="product-order-details__list__item"
          :value="item.value"
          :title="item.label"
          :has-divider="
            index !== (orderDetails?.details?.list?.length ?? 0) - 1
          "
        />
      </div>
      <div
        v-if="orderDetails?.details.status"
        class="product-order-details__status"
      >
        <div>
          <BaseText :color="NebraskaColors.elementPrimary" size="p3">
            {{ orderDetails.details.status.title }}
          </BaseText>
          <BaseText size="p6">
            {{ orderDetails.details.status.description }}
          </BaseText>
        </div>

        <InitialAvatarIcon
          class="product-order-details__avatar"
          :icon="orderDetails.details.status.icon"
          size="medium"
        />
      </div>
    </div>
    <template
      v-if="!isLoading && orderDetails?.details.footer"
      #buttonContainer
    >
      <ButtonContainer v-if="cancelModalOpen">
        <template #button-primary>
          <ButtonPrimary
            size="large"
            :label="orderDetails.details.footer.button"
            @click="handleCancelOrder"
          />
        </template>
        <template #button-secondary>
          <ButtonSecondary
            size="large"
            label="Voltar"
            @click="cancelModalOpen = false"
          />
        </template>
      </ButtonContainer>
      <ButtonContainer v-else>
        <template #button-primary>
          <ButtonSecondary
            size="large"
            :label="orderDetails.details.footer.button"
            @click="cancelModalOpen = true"
          />
        </template>
      </ButtonContainer>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';

import {
  Modal,
  LoaderIndicator,
  HelperViewStatus,
  HeadingDisplay,
  BaseText,
  ListDisplay,
  InitialAvatarIcon,
  ButtonContainer,
  ButtonPrimary,
  ButtonSecondary
} from '@warrenbrasil/nebraska-web';

import {
  getProductOrderDetails,
  cancelOrder,
  IProductOrderDetails
} from '@/modules/home/services/orders';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

interface Emits {
  (event: 'close'): void;
}

const emit = defineEmits<Emits>();

interface Props {
  isOpen: boolean;
  orderId: string;
  productType: string;
}
const props = defineProps<Props>();

const orderDetails = ref<IProductOrderDetails | null>(null);
const isLoading = ref(false);
const hasError = ref(false);
const cancelModalOpen = ref(false);

watch(
  () => props.isOpen,
  async isOpen => {
    if (isOpen) {
      fetchOrderDetails();
    }
  }
);

function handleClose() {
  cancelModalOpen.value = false;
  emit('close');
}

async function fetchOrderDetails() {
  try {
    isLoading.value = true;
    hasError.value = false;

    orderDetails.value = await getProductOrderDetails(
      props.orderId,
      props.productType
    );
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

async function handleCancelOrder() {
  try {
    isLoading.value = true;

    const response = await cancelOrder(props.orderId);

    notify({
      group: 'wrn-notifications',
      text: response.title,
      type: NotificationTypes.success
    });
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Não foi possível realizar a operação, por favor tente novamente.',
      type: NotificationTypes.error
    });
  } finally {
    isLoading.value = false;
    emit('close');
  }
}
</script>
<style lang="less" scoped>
.product-order-details {
  :deep(.base-modal--wrapper) {
    width: 683px;
    max-width: 683px;
  }

  :deep(.modal-content) {
    text-align: left;
    padding: @size-spacing-x800 @size-spacing-x1200;
  }

  &__helper {
    margin: 0 auto;
    justify-content: center;
  }

  &__list {
    margin-top: @size-spacing-x800;

    &__item {
      margin-bottom: @size-spacing-x400;
      padding-bottom: @size-spacing-x400;
    }
  }

  &__status {
    padding: @size-spacing-x500 @size-spacing-x600;
    background-color: @background-tertiary;
    display: flex;
    justify-content: space-between;
    align-content: center;
    border-radius: @size-radius-x300;
  }

  &__avatar {
    background-color: @status-info-background;
    color: @status-info;
  }
}
</style>
